import { isEmpty, isEqualWith, merge } from "lodash-es";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useAccommodationBlockLocationOptions } from "Bands/utils/hooks/useAccommodationBlockLocationOptions";
import { useSuggestedLocation } from "Bands/utils/hooks/useSuggestedLocation";
import { FormBuilder, PlacesInput } from "shared/components";
import { IAddress, IAPIState, IFormButtons } from "shared/types";
import { IVenue, IVenuePostData } from "Venues/types";
import { getFormFields, defaultValues as standardDefaultValues } from "./formData";
import { API_ENDPOINTS } from "shared/constants";

interface VenueFormProps {
  apiStatus?: IAPIState;
  hasError?: boolean;
  defaultValues?: Record<string, any>;
  buttons?: IFormButtons;
  handleSubmit: (venue: IVenuePostData) => void;
  updatePlaceholder?: (venue: IVenue) => void;
  showExtendedInfo?: boolean;
  hasContainer?: boolean;
}

export const VenueForm: React.FC<VenueFormProps> = ({
  apiStatus,
  hasError,
  defaultValues: dataDefaultValues,
  buttons,
  handleSubmit,
  updatePlaceholder,
  showExtendedInfo,
  hasContainer,
  children,
}) => {
  const defaultValues = useMemo(
    () => merge({}, standardDefaultValues, dataDefaultValues),
    [dataDefaultValues]
  );

  const [title, setTitle] = useState<string>(defaultValues?.title || "");
  const [partialVenue, setPartialVenue] = useState<IVenue | null>();
  const [address, setAddress] = useState<IAddress | null>(defaultValues?.address || null);
  const [tempAddress, setTempAddress] = useState<IAddress | null>(defaultValues?.address || null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  const preventSubmit = useMemo(() => !address, [address]);

  const { options, isFetchingLocations, fetchGoogleLocations } =
    useAccommodationBlockLocationOptions();

  const { data: selectedLocationData } = useSuggestedLocation(selectedLocationId);

  // Add location parameter in request body if address is unchanged
  const location = useMemo(() => {
    if (
      !isEqualWith(
        address,
        selectedLocationData?.address,
        (a, b) => a === b || (isEmpty(a) && isEmpty(b))
      )
    )
      return undefined;

    return API_ENDPOINTS.EVENT_SUGGESTED_LOCATION(selectedLocationData?.id || "");
  }, [selectedLocationData, address]);

  const onSubmit = (partialVenue: IVenuePostData) => {
    if (address) {
      const venue: IVenuePostData = { ...partialVenue, address, title, location };
      handleSubmit(venue);
    }
  };

  const handleBlur = (venue: IVenue) => {
    setPartialVenue(venue);

    updatePlaceholder &&
      updatePlaceholder({
        ...venue,
        ...(tempAddress && { address: tempAddress }),
      });
  };

  const handleBlurAddress = useCallback(
    (address: IAddress | null) => {
      setTempAddress(address);

      updatePlaceholder &&
        partialVenue &&
        updatePlaceholder({
          ...partialVenue,
          ...(address && { address }),
        });
    },
    [setTempAddress, partialVenue, updatePlaceholder]
  );

  const submitAddress = useCallback((address: IAddress | null) => {
    setAddress(address);
  }, []);

  const fields = useMemo(
    () =>
      getFormFields({
        showExtendedInfo,
        address,
        setAddress,
        tempAddress,
        setTempAddress,
        submitAddress,
        handleBlurAddress,
        hasContainer,
      }),
    [
      showExtendedInfo,
      address,
      setAddress,
      tempAddress,
      setTempAddress,
      submitAddress,
      handleBlurAddress,
      hasContainer,
    ]
  );

  // Map selectedLocationData.address to IAddress format and prefill form
  useEffect(() => {
    if (selectedLocationData?.address) {
      setAddress(selectedLocationData?.address as IAddress);
      setTempAddress(selectedLocationData?.address as IAddress);
      submitAddress(selectedLocationData?.address as IAddress);
    }
  }, [selectedLocationData, submitAddress]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchGoogleLocations(title);
    }, 200);

    return () => clearTimeout(debounceTimer);
  }, [title, fetchGoogleLocations]);

  return (
    <>
      <PlacesInput
        name="title"
        label="Title"
        options={options}
        onOptionSelect={option => setSelectedLocationId(option.value)}
        value={title}
        onChange={setTitle}
        isLoading={isFetchingLocations}
      />
      <FormBuilder
        fields={fields}
        defaultValues={defaultValues}
        buttons={buttons}
        onSubmit={onSubmit}
        onBlur={handleBlur}
        preventSubmit={preventSubmit}
        isSubmitting={apiStatus === IAPIState.pending}
        apiErrorMessage={hasError}
      >
        {children}
      </FormBuilder>
    </>
  );
};
